import {
  MediaModel,
  MemoryModel,
  Contact,
  ContactModel,
  GroupModel,
  getMediaType,
  DateParser,
  determineMediaCategory,
} from "common247";

/**
 * Filter criteria for filtering media items across multiple dimensions.
 */
export interface IMediaFilterCriteria {
  /** Free-text search query across title, description, filename, fileHash, or tag text */
  searchQuery?: string;
  /** Filter start date (ISO string, dd-MMM-yyyy, or epoch timestamp) */
  dateFrom?: string | number | Date | null;
  /** Filter end date (ISO string, dd-MMM-yyyy, or epoch timestamp) */
  dateTo?: string | number | Date | null;
  /** Selected Tag IDs */
  tagIds?: string[];
  /** Selected Contact IDs */
  contactIds?: string[];
  /** Selected Group IDs */
  groupIds?: string[];
  /** Selected Memory IDs */
  memoryIds?: string[];
  /** Media Category filter (standard, generated, documents, emoji, sticker) */
  mediaCategory?: string;
  /** Media Type filter (image, video, audio, document, pdf, other) */
  mediaType?: "all" | "image" | "video" | "audio" | "document" | "pdf" | "other" | string;
  /** Minimum favourite rating (1 to 5) */
  minRating?: number;
}

/**
 * Result set returned when filtering media items.
 */
export interface IFilteredMediaResultSet {
  /** Filtered media items */
  media: MediaModel[];
  /** Associated memories containing or referencing filtered media */
  memories: MemoryModel[];
  /** Associated contacts */
  contacts: (Contact | ContactModel)[];
  /** Associated groups */
  groups: GroupModel[];
  /** Aggregate counts for summary indicators */
  totalCounts: {
    media: number;
    memories: number;
    contacts: number;
    groups: number;
  };
  /** Criteria used to generate the results */
  criteria: IMediaFilterCriteria;
}

/**
 * Parses a date or timestamp into normalized epoch milliseconds.
 *
 * @param val - Date input string, number, or Date object.
 * @returns Epoch timestamp in milliseconds, or null if unparseable.
 */
export function parseDateToEpoch(val: any): number | null {
  if (!val) return null;
  if (typeof val === "number") {
    if (val > 1900 && val < 2100) {
      return new Date(val, 0, 1).getTime();
    }
    return val;
  }
  if (val instanceof Date) return val.getTime();

  const str = String(val).trim();
  if (/^\d{4}$/.test(str)) {
    return new Date(parseInt(str, 10), 0, 1).getTime();
  }

  const parsed = Date.parse(str);
  if (!isNaN(parsed)) return parsed;

  const parts = str.match(/^(\d{1,2})-([A-Za-z]{3})-(\d{4})/);
  if (parts) {
    const months = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
    const mIdx = months.indexOf(parts[2].toLowerCase());
    if (mIdx !== -1) {
      return new Date(parseInt(parts[3], 10), mIdx, parseInt(parts[1], 10)).getTime();
    }
  }

  return null;
}

/**
 * Extracts a normalized epoch timestamp for a media item using its date, mediaDate,
 * capturedAt, or createdAt fallback.
 *
 * @param media - Media item to inspect.
 * @returns Epoch timestamp in milliseconds, or null.
 */
export function getMediaEpoch(media: MediaModel): number | null {
  const rawDate = media.date;
  if (rawDate) {
    const parsed = DateParser.parse(rawDate);
    if (parsed.from && parsed.from.year) {
      const year = parsed.from.year;
      const month = (parsed.from.month ?? 1) - 1;
      const day = parsed.from.day ?? 1;
      let hours = 0;
      let minutes = 0;
      let seconds = 0;
      if (parsed.from.time) {
        const parts = parsed.from.time.split(":");
        hours = parseInt(parts[0], 10) || 0;
        minutes = parseInt(parts[1], 10) || 0;
        seconds = parseInt(parts[2], 10) || 0;
      }
      const d = new Date(year, month, day, hours, minutes, seconds);
      if (!isNaN(d.getTime())) {
        return d.getTime();
      }
    }
    const epoch = parseDateToEpoch(parsed.formatted);
    if (epoch) return epoch;
  }

  if (media.metadata?.capturedAt) {
    return media.metadata.capturedAt;
  }

  if (media.createdAt) {
    return media.createdAt;
  }

  return null;
}

/**
 * Applies multi-dimensional filtering criteria across Media items and their
 * relational contexts (Memories, Contacts, Groups, Tags, Types, Ratings).
 *
 * @param allMedia - Full source pool of media items.
 * @param allMemories - Source pool of memories for relational linking.
 * @param allContacts - Source pool of contacts.
 * @param allGroups - Source pool of groups.
 * @param criteria - Filter criteria to apply.
 * @returns {IFilteredMediaResultSet} The filtered result set and counts.
 */
export function applyMediaFilter(
  allMedia: MediaModel[] = [],
  allMemories: MemoryModel[] = [],
  allContacts: (Contact | ContactModel)[] = [],
  allGroups: GroupModel[] = [],
  criteria: IMediaFilterCriteria = {}
): IFilteredMediaResultSet {
  const query = (criteria.searchQuery || "").toLowerCase().trim();
  const fromEpoch = parseDateToEpoch(criteria.dateFrom);
  const toEpoch = parseDateToEpoch(criteria.dateTo);
  const selectedTagIds = new Set(criteria.tagIds || []);
  const selectedContactIds = new Set(criteria.contactIds || []);
  const selectedGroupIds = new Set(criteria.groupIds || []);
  const selectedMemoryIds = new Set(criteria.memoryIds || []);

  const matchingMedia = allMedia.filter((m) => {
    // 1. Text Search Filter (title, description, fileHash, fileName, tags)
    if (query) {
      const titleMatch = (m.title || "").toLowerCase().includes(query);
      const nameMatch = (m.name || "").toLowerCase().includes(query);
      const descMatch = (m.description || "").toLowerCase().includes(query);
      const hashMatch = (m.fileHash || "").toLowerCase().includes(query);
      const tagMatch = Object.values(m.tags || {}).some((t) =>
        (t.value || "").toLowerCase().includes(query)
      );

      if (!titleMatch && !nameMatch && !descMatch && !hashMatch && !tagMatch) {
        return false;
      }
    }

    // 2. Date Range Filter
    const mediaEpoch = getMediaEpoch(m);
    if (mediaEpoch) {
      if (fromEpoch !== null && mediaEpoch < fromEpoch) return false;
      if (toEpoch !== null && mediaEpoch > toEpoch) return false;
    }

    // 3. Media Type Filter
    if (criteria.mediaType && criteria.mediaType !== "all") {
      const detected = m.mediaType || getMediaType(m.contentType, m.title);
      if (detected !== criteria.mediaType) {
        return false;
      }
    }

    // 4. Media Category Filter
    if (criteria.mediaCategory && criteria.mediaCategory !== "all") {
      const cat =
        m.mediaCategory ||
        determineMediaCategory({
          contentType: m.contentType,
          fileName: m.title,
          mediaType: m.mediaType,
          metadata: m.metadata,
        });
      if (cat !== criteria.mediaCategory) {
        return false;
      }
    }

    // 5. Favourite Rating Filter
    if (criteria.minRating && criteria.minRating > 1) {
      const rating = m.favouriteRating ?? 3;
      if (rating < criteria.minRating) {
        return false;
      }
    }

    // 6. Tags Filter
    if (selectedTagIds.size > 0) {
      const mediaTagIds = new Set(
        ((m as any).tagIds || []).concat(Object.keys(m.tags || {}))
      );
      let hasMatchingTag = false;
      for (const tId of selectedTagIds) {
        if (mediaTagIds.has(tId)) {
          hasMatchingTag = true;
          break;
        }
      }
      if (!hasMatchingTag) return false;
    }

    // 7. Contacts Filter
    if (selectedContactIds.size > 0) {
      const mediaContactIds = new Set(
        ((m as any).contactIds || []).concat((m as any).sharedContactIds || [])
      );
      let hasMatchingContact = false;
      for (const cId of selectedContactIds) {
        if (mediaContactIds.has(cId)) {
          hasMatchingContact = true;
          break;
        }
      }
      if (!hasMatchingContact) return false;
    }

    // 8. Groups Filter
    if (selectedGroupIds.size > 0) {
      const mediaGroupIds = new Set(
        ((m as any).groupIds || []).concat((m as any).sharedGroupIds || [])
      );
      let hasMatchingGroup = false;
      for (const gId of selectedGroupIds) {
        if (mediaGroupIds.has(gId)) {
          hasMatchingGroup = true;
          break;
        }
      }
      if (!hasMatchingGroup) return false;
    }

    // 9. Memories Filter
    if (selectedMemoryIds.size > 0) {
      const mediaMemoryIds = new Set(
        ((m as any).memoryIds || []).concat((m as any).memoryId ? [(m as any).memoryId] : [])
      );
      let hasMatchingMemory = false;
      for (const memId of selectedMemoryIds) {
        if (mediaMemoryIds.has(memId)) {
          hasMatchingMemory = true;
          break;
        }
      }
      if (!hasMatchingMemory) return false;
    }

    return true;
  });

  // Collect associated entities from matching media
  const matchingMediaIds = new Set(matchingMedia.map((m) => m.id));

  // Associated Memories
  const associatedMemories = allMemories.filter((mem) => {
    if (Array.isArray(mem.mediaItems) && mem.mediaItems.some((item) => item.id && matchingMediaIds.has(item.id))) {
      return true;
    }
    if (Array.isArray((mem as any).media) && (mem as any).media.some((item: any) => item.id && matchingMediaIds.has(item.id))) {
      return true;
    }
    if (mem.media && typeof mem.media === "object" && !Array.isArray(mem.media)) {
      if (Object.keys(mem.media).some((id) => matchingMediaIds.has(id))) {
        return true;
      }
    }
    const memMediaIds = (mem as any).mediaIds || [];
    if (Array.isArray(memMediaIds) && memMediaIds.some((id: string) => matchingMediaIds.has(id))) {
      return true;
    }
    if (mem.photoMediaId && matchingMediaIds.has(mem.photoMediaId)) {
      return true;
    }
    return false;
  });

  // Associated Contacts
  const associatedContactIds = new Set<string>();
  matchingMedia.forEach((m) => {
    ((m as any).contactIds || []).forEach((cId: string) => associatedContactIds.add(cId));
    ((m as any).sharedContactIds || []).forEach((cId: string) => associatedContactIds.add(cId));
  });
  const associatedContacts = allContacts.filter((c) => c.id && associatedContactIds.has(c.id));

  // Associated Groups
  const associatedGroupIds = new Set<string>();
  matchingMedia.forEach((m) => {
    ((m as any).groupIds || []).forEach((gId: string) => associatedGroupIds.add(gId));
    ((m as any).sharedGroupIds || []).forEach((gId: string) => associatedGroupIds.add(gId));
  });
  const associatedGroups = allGroups.filter((g) => g.id && associatedGroupIds.has(g.id));

  return {
    media: matchingMedia,
    memories: associatedMemories,
    contacts: associatedContacts,
    groups: associatedGroups,
    totalCounts: {
      media: matchingMedia.length,
      memories: associatedMemories.length,
      contacts: associatedContacts.length,
      groups: associatedGroups.length,
    },
    criteria,
  };
}
